import { ref, get, onValue, off } from "firebase/database";
import { database } from "../firebase";

const HEARTBEAT_BASE_URL = "https://watch-api-97qr.onrender.com/core";
const ONLINE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

const TIMESTAMP_KEYS = [
  "lastOnline",
  "last_online",
  "lastSeen",
  "last_seen",
  "timestamp",
  "ts",
  "updatedAt",
  "updated_at",
];

const normaliseNumber = (value) => {
  if (typeof value !== "number") return null;
  if (!Number.isFinite(value)) return null;
  // If timestamp is in seconds (less than year 5138), convert to ms
  if (value < 1e12) {
    return value * 1000;
  }
  return value;
};

const parseDateFromValue = (value) => {
  if (value == null) return null;

  if (typeof value === "number") {
    const ms = normaliseNumber(value);
    return ms ? new Date(ms) : null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      const ms = normaliseNumber(numeric);
      if (ms) return new Date(ms);
    }

    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return null;
};

const findTimestamp = (payload) => {
  if (payload == null) return null;
  if (typeof payload === "string" || typeof payload === "number") {
    return parseDateFromValue(payload);
  }

  for (const key of TIMESTAMP_KEYS) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      const date = parseDateFromValue(payload[key]);
      if (date) return date;
    }
  }

  return null;
};

const buildStatus = (timestamp) => {
  if (!(timestamp instanceof Date) || Number.isNaN(timestamp.getTime())) {
    return {
      isOnline: false,
      lastOnline: null,
      lastOnlineMs: null,
    };
  }

  const lastOnlineMs = timestamp.getTime();
  const isOnline = Date.now() - lastOnlineMs <= ONLINE_THRESHOLD_MS;

  return {
    isOnline,
    lastOnline: timestamp.toISOString(),
    lastOnlineMs,
  };
};

const callHeartbeatEndpoint = async (url, options = {}) => {
  const controller = new AbortController();
  const { signal, ...rest } = options;

  if (signal) {
    signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  const response = await fetch(url, {
    ...rest,
    method: rest.method ?? "GET",
    signal: controller.signal,
  });

  if (!response.ok) {
    const error = new Error(
      `Heartbeat request failed with status ${response.status}`
    );
    error.status = response.status;
    throw error;
  }
};

const readLastOnlineValue = async (watchId) => {
  const nodeRef = ref(database, `Watches/${watchId}/lastOnline`);
  const snapshot = await get(nodeRef);
  if (!snapshot.exists()) return null;
  return snapshot.val();
};

const toStatus = (raw) => {
  const timestamp = findTimestamp(raw);
  return {
    ...buildStatus(timestamp),
    raw,
  };
};

export const fetchHeartbeatForWatch = async (watchId, { signal } = {}) => {
  if (!watchId) throw new Error("watchId is required");

  await callHeartbeatEndpoint(
    `${HEARTBEAT_BASE_URL}/${encodeURIComponent(watchId)}/hb`,
    { signal }
  );

  const raw = await readLastOnlineValue(watchId);
  return toStatus(raw);
};

export const fetchHeartbeatForAll = async (watchIds, { signal } = {}) => {
  const ids = Array.isArray(watchIds)
    ? Array.from(new Set(watchIds.filter((id) => typeof id === "string" && id)))
    : [];

  if (ids.length === 0) {
    await callHeartbeatEndpoint(`${HEARTBEAT_BASE_URL}/hb`, { signal });
    return {};
  }

  await callHeartbeatEndpoint(`${HEARTBEAT_BASE_URL}/hb`, { signal });

  const entries = await Promise.all(
    ids.map(async (watchId) => {
      try {
        const raw = await readLastOnlineValue(watchId);
        return [watchId, toStatus(raw)];
      } catch (error) {
        console.error(
          `Failed to read lastOnline node for watch ${watchId}`,
          error
        );
        return [watchId, toStatus(null)];
      }
    })
  );

  return Object.fromEntries(entries);
};

export const subscribeToHeartbeatStatus = (watchId, callback) => {
  if (!watchId || typeof callback !== "function") return () => {};

  const nodeRef = ref(database, `Watches/${watchId}/lastOnline`);
  const handler = onValue(
    nodeRef,
    (snapshot) => {
      const raw = snapshot.exists() ? snapshot.val() : null;
      callback(toStatus(raw));
    },
    (error) => {
      console.error(
        `Heartbeat subscription failed for watch ${watchId}`,
        error
      );
      callback(toStatus(null));
    }
  );

  return () => {
    off(nodeRef, "value", handler);
  };
};

export const subscribeToHeartbeatStatuses = (watchIds, callback) => {
  if (typeof callback !== "function") return () => {};

  const ids = Array.isArray(watchIds)
    ? Array.from(new Set(watchIds.filter((id) => typeof id === "string" && id)))
    : [];

  if (ids.length === 0) return () => {};

  const unsubscribers = ids.map((watchId) =>
    subscribeToHeartbeatStatus(watchId, (status) => callback(watchId, status))
  );

  return () => {
    unsubscribers.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (error) {
        console.error("Failed to unsubscribe from heartbeat status", error);
      }
    });
  };
};

export const ONLINE_STATUS_THRESHOLD_MINUTES = ONLINE_THRESHOLD_MS / 60000;

export default {
  fetchHeartbeatForWatch,
  fetchHeartbeatForAll,
  subscribeToHeartbeatStatus,
  subscribeToHeartbeatStatuses,
  ONLINE_STATUS_THRESHOLD_MINUTES,
};
